import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { Download, Check, Send, Loader2, MessageSquare, ClipboardList, FileText, Mail, AlertCircle, ChevronDown, ChevronUp, ChevronsDownUp, ChevronsUpDown, Sparkles, ScrollText, MapPin, Shield, ShieldCheck, Briefcase, HeartPulse, GraduationCap, Calculator, Home, Car, Truck, Users, CalendarDays, Lightbulb, TriangleAlert, Quote, Building2, Phone, Globe, Compass } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../context/AuthContext';
import PortalLayout from './PortalLayout';
import ViatorCtaCard from '../../components/ViatorCtaCard';
import ChecklistPanel from '../../components/portal/ChecklistPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// iter 184 - `categoryLabels` used to feed the Blueprint page's inline
// checklist renderer. The renderer now lives in `<ChecklistPanel />` and
// carries its own `CATEGORY_META`. Left here in case some other section
// of this page needs it, but currently unused.

const suggestedQuestions = [
  'What should I do first?',
  'Which neighborhood is right for me?',
  'How do I get my visa?',
  'How do I transfer money to Mexico?',
];

/**
 * iter 94 - rotating Mexico relocation facts shown on the Blueprint
 * generation loading screen. The user can be sitting on this view for
 * 3-5 minutes; rather than dead spinner time, surface useful priming for
 * the move. Order is randomized once on mount so consecutive generations
 * don't always start with the same fact.
 */
const MEXICO_FACTS = [
  "Mexico's Residente Temporal visa allows you to stay up to 4 years before applying for permanent residency.",
  'The average cost of living in Mexico City is 60–70% lower than New York or San Francisco.',
  'Mexico has over 30 UNESCO World Heritage Sites - more than any other country in the Americas.',
  'IMSS public healthcare covers most expats with residency for around $500/year.',
  'Mexico has no federal capital gains tax on primary residence sales.',
  'Over 1.5 million Americans currently live in Mexico - the largest expat community in the world.',
  'Fiber internet in major Mexican cities averages 300 Mbps and costs around $30/month.',
  'Mexico City has more museums per capita than almost any city in the world.',
];

/**
 * iter 95 - city-specific facts. Shown in place of the generic Mexico
 * facts when we know the user's destination (`blueprint.city` →
 * `user.destination_city`). Each city covers four angles per the brief:
 *   - cost of living
 *   - expat community
 *   - unique lifestyle benefit
 *   - one surprising local fact
 *
 * Keys are normalized via `_cityKey()` (lowercase + accents stripped +
 * common abbreviations folded) so input variants like "CDMX",
 * "Mérida", "merida", "Mexico City" all map to the same entry.
 */
const CITY_FACTS = {
  'mexico city': [
    'Mexico City rent averages $700–$1,200/month for a furnished one-bedroom in Roma or Condesa - about a quarter of comparable NYC apartments.',
    'Polanco, Roma Norte, Condesa and Coyoacán host one of the largest digital-nomad communities in Latin America, with hundreds of co-working spots and weekly expat meetups.',
    "World-class metro for ~$0.30 a ride, walkable tree-lined neighborhoods and a year-round climate that rarely leaves the 60s–80s°F - there's no off-season.",
    'Surprising fact: Mexico City has more museums (over 150) than almost any other city on Earth and sinks roughly 20 inches per year because it sits on a drained lake bed.',
  ],
  'guadalajara': [
    'Guadalajara is ~30% cheaper than Mexico City - a furnished apartment in Providencia or Lafayette runs $500–$900/month.',
    "Mexico's second-largest tech hub ('Silicon Valley of Mexico') draws thousands of remote workers and software engineers; Chapala/Ajijic nearby is one of the oldest American expat enclaves.",
    'Walkable historic center, year-round spring climate (60s–80s°F), and the country\'s deepest cultural roots - birthplace of mariachi, tequila, and charreria.',
    'Surprising fact: tequila can ONLY legally be made in Jalisco state - Guadalajara sits an hour from the agave fields of the town that gave the spirit its name.',
  ],
  'monterrey': [
    'Monterrey has the highest GDP per capita in Mexico; rent in San Pedro Garza García runs $900–$1,500 but services match U.S. quality.',
    'Strong corporate-expat presence (Tec de Monterrey, manufacturing executives, U.S. tech) - English is widely spoken in San Pedro and downtown.',
    'Surrounded by the Sierra Madre - hiking, climbing, and Chipinque park are 20 minutes from downtown high-rises.',
    'Surprising fact: Monterrey has the busiest international border crossing for cargo in Latin America and an outsized influence on the Mexican economy - locals call it "the Detroit of Mexico."',
  ],
  'puerto vallarta': [
    'A two-bedroom condo with ocean views rents for $1,200–$2,000/month; full-time living is roughly 50% below comparable U.S. coastal towns.',
    'One of the largest North-American retiree and LGBTQ+ communities in Mexico - over 50,000 expats, English near-universal in Zona Romántica and Marina Vallarta.',
    "Walkable Malecón boardwalk, year-round 75–85°F sea, and direct flights to 30+ U.S./Canadian cities - you don't need a car.",
    "Surprising fact: Puerto Vallarta was a sleepy fishing village until 1963 when the film *The Night of the Iguana* put it on the map and triggered Mexico's tourism boom.",
  ],
  'tulum': [
    'Tulum is the most expensive Mexican beach town - premium boutique condos run $2,000–$3,500/month, though inland colonias still rent at $700–$1,000.',
    'Heavily nomad and wellness-driven: yoga teachers, crypto founders, and remote creators dominate the expat scene; coworking spaces line the jungle road.',
    'Bike everywhere, swim in cenotes daily, and live 10 minutes from one of the only Mayan ruin sites perched directly on a Caribbean cliff.',
    'Surprising fact: Tulum sits atop the Yucatán\'s freshwater cenote system - the longest underwater cave network in the world, with over 250 miles mapped to date.',
  ],
  'oaxaca': [
    'Oaxaca is one of the cheapest cities on this list - a beautiful colonial-center one-bedroom rents for $400–$700/month.',
    'A growing artist + slow-living expat community (around 10,000 foreigners) - smaller and more integrated than Mexico City, with strong ties to indigenous Zapotec culture.',
    'Walkable UNESCO historic center, the most celebrated food scene in Mexico (mole, mezcal, tlayudas), and the famed Día de los Muertos festival that draws visitors worldwide.',
    'Surprising fact: Oaxaca state has 16 officially recognized indigenous languages still spoken daily - the highest cultural and linguistic diversity in all of Mexico.',
  ],
  'san miguel de allende': [
    "San Miguel runs about 25% above the national average - colonial-center rentals are $1,000–$1,800/month, with cheaper options in nearby colonias.",
    'Around 10,000 American and Canadian residents - one of the highest expat ratios per capita in Mexico, and Travel + Leisure has named it "Best City in the World" multiple times.',
    'Cobblestone streets, Cormorant-perfect colonial architecture, year-round mid-70s°F climate, no beach but a thriving art, music, and culinary scene.',
    'Surprising fact: the entire historic core is a UNESCO World Heritage Site, and city ordinances forbid traffic lights or stop signs in centro - drivers yield by tradition alone.',
  ],
  'merida': [
    'Mérida is the safest large city in Mexico - and one of the cheapest: a colonial home in centro rents for $600–$1,000/month, or buy for $150K–$300K.',
    'Fastest-growing American expat city in Mexico - over 12,000 foreigners and a noticeable surge of remote workers fleeing higher-cost beach towns.',
    'Hot but easy living: bike-friendly streets, Sunday-night plaza parties (every weekend), 30 minutes to Gulf beaches and 90 minutes to Caribbean cenotes.',
    'Surprising fact: Mérida has been ranked the safest city in Mexico (and the second-safest in the Americas after Quebec City) by independent indexes for over a decade.',
  ],
  'cabo san lucas': [
    'Cabo skews resort-priced - beachfront condos run $1,500–$3,500/month, but inland San José del Cabo neighborhoods stay under $1,000.',
    'Heavily American - over 40,000 U.S. expats split between Cabo and San José del Cabo, with English used almost everywhere in tourist zones.',
    'Pacific desert meets the Sea of Cortez: world-class sport fishing, whale-watching November–April, and 350+ days of sunshine a year.',
    'Surprising fact: Cabo sits at the tip of the Baja peninsula on the only point where the Pacific and the Sea of Cortez meet - Jacques Cousteau called the latter "the world\'s aquarium."',
  ],
  'playa del carmen': [
    'A furnished condo a few blocks from the beach rents for $900–$1,500/month - about half what Tulum charges for similar finishes.',
    'Most international city in the Yucatán: ~25% of residents are foreign, with strong European and Argentinian communities alongside Americans and Canadians.',
    'Walk-everywhere downtown, Caribbean swim year-round, and the ferry to Cozumel for diving leaves every hour from the town center.',
    "Surprising fact: Playa was a fishing village of 1,500 in 1980. It's now the fastest-growing city in Latin America, doubling its population every decade.",
  ],
  'mazatlan': [
    'One of the best Pacific-coast values: a one-bedroom in the Centro Histórico or Golden Zone rents for $500–$900/month.',
    'Growing snowbird and full-time American/Canadian community (around 20,000) - Old Mazatlán has a tight-knit, low-key expat scene without the resort crowds of Cabo or PV.',
    'The longest oceanfront malecón in the Americas (13 miles), affordable fresh seafood, and Mexico\'s most colorful Carnaval celebration every February.',
    'Surprising fact: Mazatlán hosts the third-largest Carnaval in the world - only Rio de Janeiro and New Orleans pull bigger crowds.',
  ],
  'queretaro': [
    'Querétaro is one of the best cost-to-quality ratios in Mexico - a modern apartment in Juriquilla or El Refugio rents for $600–$1,000/month.',
    'Smaller but rapidly growing expat community, especially around aerospace and automotive sectors - Bombardier and Safran have major plants here.',
    'Compact UNESCO colonial center, perfect mid-70s°F year-round, and 90 minutes from Mexico City\'s airport without the megacity traffic.',
    'Surprising fact: Querétaro is at the heart of Mexico\'s emerging wine country - over 30 vineyards in the surrounding valleys and rapidly winning international medals.',
  ],
  'la paz': [
    'La Paz is meaningfully cheaper than Cabo (3 hours south) - a furnished one-bedroom near the malecón rents for $600–$1,000/month.',
    'Smaller, more local expat community than other Baja towns - roughly 5,000 foreigners with a strong sailing, kiteboarding, and slow-living crowd.',
    'Calm Sea of Cortez beaches you can drive to alone, swim with whale sharks October–April, and one of the lowest crime rates of any Mexican beach city.',
    "Surprising fact: La Paz is the capital of Baja California Sur but feels like a small town - and the Sea of Cortez right outside it was named one of UNESCO's most biodiverse marine ecosystems on Earth.",
  ],
  'todos santos': [
    'A "Pueblo Mágico" pricier than nearby La Paz but cheaper than Cabo - restored adobe casitas rent for $800–$1,500/month.',
    'Tiny but tight expat scene of around 1,500 foreigners - heavily artists, surfers, and retirees seeking quiet over nightlife.',
    'A walkable historic core, big Pacific surf 5 minutes away, and a desert-meets-ocean climate that stays in the 70s most of the year.',
    'Surprising fact: locals will tell you the "Hotel California" of Eagles fame is in Todos Santos - the actual lyrical reference is up for debate, but the hotel of that name is now one of the town\'s most-photographed landmarks.',
  ],
  'ensenada': [
    'Ensenada is one of the cheapest places to live within driving distance of California - a furnished apartment near the malecón rents for $500–$900/month.',
    'Heavy weekend American presence (San Diego is 80 miles north) but a smaller full-time expat community - around 10,000 foreigners, many tied to wine, fishing, or remote work.',
    'Mediterranean climate, world-class surf at neighboring San Miguel and Salsipuedes, and the only major wine region in Mexico right at your doorstep in the Valle de Guadalupe.',
    'Surprising fact: Ensenada\'s Valle de Guadalupe produces over 70% of all Mexican wine and is increasingly compared to Napa - but with a fraction of the price tag.',
  ],
};

/** Normalize a city string into a `CITY_FACTS` lookup key. Strips
 * accents, lowercases, trims, and folds a few common abbreviations
 * (CDMX → mexico city, etc.). Returns `null` if input is empty.
 */
const _cityKey = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  let s = raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
  if (!s) return null;
  // Drop any country/state suffix the user may have entered.
  s = s.split(',')[0].trim();
  // Common abbreviation folds.
  const FOLDS = {
    'cdmx': 'mexico city',
    'df': 'mexico city',
    'mexico df': 'mexico city',
    'ciudad de mexico': 'mexico city',
    'cd. de mexico': 'mexico city',
    'gdl': 'guadalajara',
    'mty': 'monterrey',
    'pv': 'puerto vallarta',
    'cabo': 'cabo san lucas',
    'los cabos': 'cabo san lucas',
    'san jose del cabo': 'cabo san lucas',
    'playa': 'playa del carmen',
    'sma': 'san miguel de allende',
    'san miguel': 'san miguel de allende',
  };
  return FOLDS[s] || s;
};

/** Resolve a destination string to its 4-fact list, or fall back to the
 * generic 8-fact MEXICO_FACTS when the city is unknown / unsupported. */
const factsForCity = (rawCity) => {
  const key = _cityKey(rawCity);
  if (key && CITY_FACTS[key]) return CITY_FACTS[key];
  return MEXICO_FACTS;
};

/**
 * iter 103 - Editorial-grade Blueprint rendering.
 *
 * Map a section title to a lucide icon. Order matters - first match wins.
 * Keep the regexes loose so the LLM's slightly-varied headings still resolve
 * (e.g. "Visa & Legal Pathway" / "2. Visa & Legal Pathway" / "Your Legal
 * Pathway" all hit the Shield icon).
 */
const SECTION_ICON_RULES = [
  [/welcome|introduction/i, Sparkles],
  [/glance|overview|relocation at/i, Compass],
  [/visa|legal|immigration|residenc/i, Shield],
  [/neighborhood|where to live|areas/i, MapPin],
  [/trendy|nightlife|food|restaurants|where to eat/i, Building2],
  [/banking|bank|finance|money|currency/i, Briefcase],
  [/healthcare|hospital|doctor|medical|wellness/i, HeartPulse],
  [/insurance/i, Shield],
  [/school|education|kids|children/i, GraduationCap],
  [/tax|rfc|fiscal/i, Calculator],
  [/housing|rental|rent|apartment|property/i, Home],
  [/car|vehicle|driving|transport/i, Car],
  [/moving|move|relocation plan|logistics/i, Truck],
  [/community|social|expat|friends|networking/i, Users],
  [/checklist|30.day|action|next steps|timeline/i, CalendarDays],
  [/vs|comparison|backup/i, Compass],
];

const getSectionIcon = (title) => {
  if (!title) return ScrollText;
  for (const [re, Icon] of SECTION_ICON_RULES) {
    if (re.test(title)) return Icon;
  }
  return ScrollText;
};

/**
 * Strip a leading numeric prefix (e.g. "2. Visa & Legal Pathway" or
 * "1a. CDMX vs Your Backup Cities") so we can show it as a separate badge.
 * Returns `{ number, cleanTitle }`.
 */
const splitSectionNumber = (title = '') => {
  const m = title.match(/^(\d+[a-z]?)\.\s*(.+)$/);
  if (m) return { number: m[1], cleanTitle: m[2] };
  return { number: null, cleanTitle: title };
};

/**
 * Split a Blueprint markdown document into sections by H2 (`## `) headings.
 * Returns `[{ title, body }]`. The body is the raw markdown between this H2
 * and the next one - passed straight to ReactMarkdown so lists, bold,
 * tables, links, code blocks, and inline H3+ all render normally.
 *
 * Falls back to a single "Your Blueprint" section if the content has no H2
 * markers (legacy single-shot generations or pasted content).
 */
const parseBlueprintSections = (md) => {
  if (!md || typeof md !== 'string') return [];
  const lines = md.split('\n');
  const sections = [];
  let preamble = '';
  let current = null;
  for (const line of lines) {
    const m = line.match(/^##\s+(.+?)\s*$/);
    if (m) {
      if (current) sections.push(current);
      current = { title: m[1].trim(), body: '' };
    } else if (current) {
      current.body += line + '\n';
    } else {
      preamble += line + '\n';
    }
  }
  if (current) sections.push(current);
  if (sections.length === 0 && preamble.trim()) {
    return [{ title: 'Your Blueprint', body: preamble }];
  }
  return sections.map((s) => ({ ...s, body: s.body.replace(/\s+$/g, '') }));
};

/* ============================================================
 * iter 103 - Editorial helpers
 *
 * The Blueprint generator returns straight Markdown. To make the portal
 * feel like a $500/hr consultant report (rather than a wall of text), we
 * post-process the parsed React tree on a per-paragraph basis. Three
 * patterns we detect:
 *
 *   1. Callout paragraphs - paragraphs whose first text node starts with a
 *      recognised marker word (`Tip:`, `Warning:`, `Did you know?`, etc.).
 *      Rendered as themed boxes with an icon.
 *   2. Vendor / contact cards - paragraphs that lead with a <strong> AND
 *      contain a contact keyword (Address, Phone, Website, www., http,
 *      Email, Tel). Rendered as a styled card with the bold as the
 *      business name and a "verify before visiting" disclaimer.
 *   3. Currency pills - inline tokens like "$3,500", "MXN 25,000",
 *      "USD 1,200". Wrapped in a subtle pill badge inside the paragraph.
 *
 * All three handlers fall back to the plain editorial paragraph style if
 * the pattern doesn't match - so nothing breaks if the LLM's output
 * shifts.
 * ============================================================ */

const CALLOUT_PATTERNS = [
  { re: /^(tip|insider|heads up|good to know|pro tip|note)\s*[:!--]?\s*/i, kind: 'teal' },
  { re: /^(warning|important|caution|deadline|don'?t miss|watch out|red flag|legal requirement)\s*[:!--]?\s*/i, kind: 'amber' },
  { re: /^(quick win|easy step|done|confirmed|good news|easy|win)\s*[:!--]?\s*/i, kind: 'green' },
  { re: /^did you know\??\s*[:!--]?\s*/i, kind: 'quote' },
];

const CONTACT_RE = /(address|phone|tel\.?|website|email|www\.|https?:\/\/|@)/i;
const CURRENCY_RE = /(\$\s?[\d,]+(?:\.\d{1,2})?(?:\s?(?:USD|MXN))?|MXN\s?\$?[\d,]+(?:\.\d{1,2})?|USD\s?\$?[\d,]+(?:\.\d{1,2})?|[\d,]+\s?pesos?)/gi;

/** Recursively join a React child tree into a plain text string. */
const childrenText = (children) => {
  let out = '';
  React.Children.forEach(children, (c) => {
    if (c == null) return;
    if (typeof c === 'string' || typeof c === 'number') out += String(c);
    else if (c.props && c.props.children != null) out += childrenText(c.props.children);
  });
  return out;
};

/** Wrap any currency tokens in a string with a styled pill. */
const wrapCurrency = (text, keyBase) => {
  if (typeof text !== 'string' || !CURRENCY_RE.test(text)) return text;
  CURRENCY_RE.lastIndex = 0;
  const out = [];
  let lastIdx = 0;
  let m;
  let i = 0;
  while ((m = CURRENCY_RE.exec(text)) !== null) {
    if (m.index > lastIdx) out.push(text.slice(lastIdx, m.index));
    out.push(
      <span
        key={`${keyBase}-cur-${i++}`}
        className="inline-flex items-baseline px-2 py-0.5 mx-0.5 rounded-full bg-[#F4ECD8] border border-[#E8A830]/30 text-[#0A1F2E] font-mono text-[0.9em] font-semibold whitespace-nowrap"
      >
        {m[0]}
      </span>,
    );
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) out.push(text.slice(lastIdx));
  return out;
};

/** Walk a React tree and replace string nodes that contain currency tokens. */
const decorateCurrency = (children, keyBase = 'p') => {
  return React.Children.map(children, (c, i) => {
    if (typeof c === 'string') return <React.Fragment key={`${keyBase}-${i}`}>{wrapCurrency(c, `${keyBase}-${i}`)}</React.Fragment>;
    if (c && c.props && c.props.children != null) {
      return React.cloneElement(c, {
        children: decorateCurrency(c.props.children, `${keyBase}-${i}`),
      });
    }
    return c;
  });
};

/** Render one of the four callout flavours. */
const Callout = ({ kind, label, children }) => {
  const cfg = {
    teal:  { Icon: Lightbulb,     border: 'border-[#2CA8A0]', bg: 'bg-[#FCF8EE]', accent: 'text-[#1A5C6E]', label: label || 'Tip' },
    amber: { Icon: TriangleAlert, border: 'border-[#E8A830]', bg: 'bg-[#FBF1DC]', accent: 'text-[#8A5A0F]', label: label || 'Important' },
    green: { Icon: Check,         border: 'border-[#2CA8A0]', bg: 'bg-[#E9F5F2]', accent: 'text-[#0F6B5C]', label: label || 'Quick win' },
    quote: { Icon: Quote,         border: 'border-[#2CA8A0]', bg: 'bg-transparent', accent: 'text-[#1A5C6E]', label: label || 'Did you know?' },
  }[kind] || { Icon: Lightbulb, border: 'border-[#2CA8A0]', bg: 'bg-[#FCF8EE]', accent: 'text-[#1A5C6E]', label: label || 'Tip' };

  if (kind === 'quote') {
    return (
      <figure className="my-5 pl-5 border-l-[3px] border-[#2CA8A0]">
        <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#1A5C6E] mb-1.5 flex items-center gap-1.5">
          <cfg.Icon className="w-3 h-3" /> {cfg.label}
        </p>
        <blockquote className="font-display italic text-lg sm:text-xl text-[#0A1F2E]/90 leading-snug">
          {children}
        </blockquote>
      </figure>
    );
  }

  return (
    <aside className={`my-4 rounded-r-lg border-l-4 ${cfg.border} ${cfg.bg} px-4 py-3.5`}>
      <p className={`font-mono uppercase tracking-[0.15em] text-[10px] mb-1.5 flex items-center gap-1.5 ${cfg.accent}`}>
        <cfg.Icon className="w-3.5 h-3.5" /> {cfg.label}
      </p>
      <div className="text-[#0A1F2E]/85 leading-relaxed text-[15px] sm:text-base">
        {children}
      </div>
    </aside>
  );
};

/** Strip the recognised marker prefix from the first text node so the
 *  rendered callout body doesn't show "Tip:" twice. */
const stripLeadingMarker = (children, re) => {
  let stripped = false;
  return React.Children.map(children, (c, i) => {
    if (stripped) return c;
    if (typeof c === 'string') {
      const after = c.replace(re, '');
      if (after !== c) stripped = true;
      return after;
    }
    if (c && c.props && c.props.children != null) {
      return React.cloneElement(c, {
        key: `s-${i}`,
        children: stripLeadingMarker(c.props.children, re),
      });
    }
    return c;
  });
};

/** Vendor / contact card. Uses the markdown AST `node` to pull the first
 *  <strong> as the business name, then renders the remaining inline
 *  children as the description body. */
const VendorCard = ({ node, children }) => {
  // 1. Get the business name from the AST so we don't rely on our
  //    overridden <strong> component identity (which is what tripped up
  //    iter 103 v1).
  const astKids = node?.children || [];
  const strongAst = astKids.find((k) => k.tagName === 'strong');
  const name = strongAst
    ? (strongAst.children || [])
        .map((c) => (c.type === 'text' ? c.value : ''))
        .join('')
        .trim()
    : null;

  // 2. Drop the first rendered child (the <strong>) and any
  //    leading-punctuation artefact ("- ", ": ", etc.) so the body
  //    flows cleanly after the strong-name moves into the header.
  const rendered = React.Children.toArray(children);
  let body = rendered;
  if (rendered.length && rendered[0]?.props) {
    // The first rendered element is the strong (whichever object identity
    // ReactMarkdown gave it). Drop it.
    body = rendered.slice(1);
    if (typeof body[0] === 'string') {
      body = [body[0].replace(/^\s*[-–:.,\s]+/, ''), ...body.slice(1)];
    }
  }

  return (
    <div className="my-4 rounded-xl bg-white border border-[#EFE6CF] shadow-[0_1px_0_rgba(10,31,46,0.04)] overflow-hidden">
      <div className="px-5 py-4 bg-[#FCF8EE] border-b border-[#EFE6CF] flex items-start gap-2.5">
        <Building2 className="w-4 h-4 text-[#1A5C6E] mt-0.5 shrink-0" />
        <div>
          <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#1A5C6E] mb-0.5">
            Vendor / Contact
          </p>
          <h4 className="font-display italic text-lg text-[#0A1F2E] leading-tight">
            {name || 'Listed contact'}
          </h4>
        </div>
      </div>
      <div className="px-5 py-4 text-[#0A1F2E]/85 leading-relaxed text-[15px]">
        {decorateCurrency(body, 'vendor')}
        <p className="mt-3 text-[11px] italic text-[#0A1F2E]/45 flex items-start gap-1.5">
          <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
          Verify hours, address and current pricing directly with the vendor before visiting.
        </p>
      </div>
    </div>
  );
};

/**
 * iter 89/103: explicit ReactMarkdown component overrides for Blueprint body
 * content. We don't ship `@tailwindcss/typography`, so without these the
 * markdown rendered as a wall of unstyled text. iter 103 adds editorial
 * polish: callouts, vendor cards, currency pills, alternating-row tables.
 */
const blueprintMarkdownComponents = {
  h1: ({ node, ...props }) => (
    <h1 className="font-display italic text-3xl text-[#0A1F2E] mt-6 mb-3 leading-tight" {...props} />
  ),
  h2: ({ node, ...props }) => (
    // H2 inside a section body shouldn't normally happen (we already split on ##),
    // but render defensively if it does.
    <h2 className="font-display italic text-2xl text-[#0A1F2E] mt-6 mb-3 leading-tight" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="font-display italic text-xl sm:text-2xl text-[#1A5C6E] mt-7 mb-3 leading-snug flex items-baseline gap-2 before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full before:bg-[#2CA8A0] before:shrink-0 before:self-center" {...props} />
  ),
  h4: ({ node, ...props }) => (
    <h4 className="font-sans font-semibold text-base text-[#0A1F2E] mt-5 mb-2 uppercase tracking-wide text-[13px]" {...props} />
  ),
  p: ({ node, children, ...props }) => {
    const text = childrenText(children).trim();

    // 1. Callout detection.
    for (const { re, kind } of CALLOUT_PATTERNS) {
      const m = text.match(re);
      if (m) {
        const stripped = stripLeadingMarker(children, re);
        const decorated = decorateCurrency(stripped, 'callout');
        const label = m[0].replace(/[:!--]\s*$/, '').replace(/\s+$/, '');
        return <Callout kind={kind} label={label}>{decorated}</Callout>;
      }
    }

    // 2. Vendor / contact card detection. Inspect the markdown AST (from
    //    ReactMarkdown's `node` prop) rather than the rendered child types
    //    - `children[0].type` is our overridden <strong> component, not
    //    the literal 'strong' string. The AST exposes `tagName: 'strong'`
    //    directly, which is what we actually want to test.
    const astKids = node?.children || [];
    const leadsWithStrong = astKids.length > 0 && astKids[0].tagName === 'strong';
    if (leadsWithStrong && CONTACT_RE.test(text)) {
      // Extract the strong's plain text to validate it looks like a name.
      const firstStrongText = (astKids[0].children || [])
        .map((c) => (c.type === 'text' ? c.value : ''))
        .join('')
        .trim();
      if (firstStrongText.length >= 2 && !/^(tip|warning|important|note|did you know|address|phone|website|email|tel)/i.test(firstStrongText)) {
        return <VendorCard node={node}>{children}</VendorCard>;
      }
    }

    // 3. Plain editorial paragraph with currency pills.
    return (
      <p className="text-[#0A1F2E]/85 leading-relaxed my-3 text-[15px] sm:text-base" {...props}>
        {decorateCurrency(children, 'p')}
      </p>
    );
  },
  strong: ({ node, ...props }) => (
    <strong className="text-[#0A1F2E] font-semibold" {...props} />
  ),
  em: ({ node, ...props }) => (
    <em className="text-[#1A5C6E] not-italic font-medium" {...props} />
  ),
  a: ({ node, ...props }) => (
    <a
      target="_blank"
      rel="noopener noreferrer"
      className="text-[#1A5C6E] underline decoration-[#2CA8A0]/40 underline-offset-2 hover:decoration-[#2CA8A0] transition"
      {...props}
    />
  ),
  ul: ({ node, ordered, ...props }) => (
    <ul className="list-disc pl-5 my-3 space-y-1.5 marker:text-[#2CA8A0]" {...props} />
  ),
  ol: ({ node, ordered, ...props }) => (
    <ol className="list-decimal pl-5 my-3 space-y-1.5 marker:text-[#1A5C6E] marker:font-mono" {...props} />
  ),
  li: ({ node, ordered, checked, children, ...props }) => (
    <li className="text-[#0A1F2E]/85 leading-relaxed text-[15px] sm:text-base pl-1" {...props}>
      {decorateCurrency(children, 'li')}
    </li>
  ),
  blockquote: ({ node, children, ...props }) => (
    // iter 103: render markdown blockquotes as the editorial pull-quote
    // variant (same as the "Did you know?" callout).
    <figure className="my-5 pl-5 border-l-[3px] border-[#2CA8A0]" {...props}>
      <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#1A5C6E] mb-1.5 flex items-center gap-1.5">
        <Quote className="w-3 h-3" /> Insight
      </p>
      <blockquote className="font-display italic text-lg sm:text-xl text-[#0A1F2E]/90 leading-snug">
        {children}
      </blockquote>
    </figure>
  ),
  hr: () => <hr className="my-6 border-0 border-t border-[#EFE6CF]" />,
  code: ({ node, inline, className, children, ...props }) =>
    inline ? (
      <code className="bg-[#FCF8EE] text-[#1A5C6E] px-1.5 py-0.5 rounded text-[0.9em] font-mono" {...props}>
        {children}
      </code>
    ) : (
      <code className="block bg-[#FCF8EE] text-[#0A1F2E] p-4 rounded-lg text-sm font-mono whitespace-pre-wrap break-words my-3" {...props}>
        {children}
      </code>
    ),
  pre: ({ node, ...props }) => <pre className="my-3" {...props} />,
  table: ({ node, ...props }) => (
    <div className="my-5 overflow-x-auto rounded-lg border border-[#EFE6CF] shadow-[0_1px_0_rgba(10,31,46,0.03)]">
      <table className="min-w-full text-sm border-collapse" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="bg-[#1A5C6E] text-white" {...props} />,
  tbody: ({ node, ...props }) => (
    <tbody
      className="[&>tr:nth-child(odd)]:bg-white [&>tr:nth-child(even)]:bg-[#FCF8EE]"
      {...props}
    />
  ),
  th: ({ node, ...props }) => (
    <th className="px-3.5 py-2.5 text-left font-mono uppercase tracking-[0.08em] text-[11px] text-white border-b border-[#0F4A58]" {...props} />
  ),
  td: ({ node, children, ...props }) => (
    <td className="px-3.5 py-2.5 text-[#0A1F2E]/85 border-b border-[#EFE6CF]/60 align-top leading-relaxed" {...props}>
      {decorateCurrency(children, 'td')}
    </td>
  ),
};

export default function PortalBlueprint() {
  const { user, loading: authLoading } = useAuth();
  const [params] = useSearchParams();

  // iter 100 - Auth guard runs FIRST, before any data-fetch / loading
  // state. While `useAuth` is still bootstrapping the JWT we render
  // nothing (parent <PortalLayout> already shows the chrome). The moment
  // auth resolves with no user, redirect to the portal login. This
  // mirrors every other portal page (PortalDashboard, PortalSubscription,
  // PortalSessions, PortalShortlist) and stops the "stuck on Loading
  // your Blueprint…" screen that paying buyers were seeing when their
  // auth cookie hadn't loaded yet.
  // iter 100 - auth guard moved BELOW state declarations in iter 103 to
  // satisfy the rules-of-hooks (must be the same hook order every render).
  // The early return is now between the state hooks and the effect hooks,
  // which is still before any data fetch, so the "stuck on Loading…"
  // symptom for un-authenticated users is still prevented.
  const bpId = params.get('id');
  const [tab, setTab] = useState('blueprint');
  const [blueprint, setBlueprint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  // iter 89: Blueprint sections - collapsible cards driven by H2 markers.
  // Default: first section open, rest collapsed. User can toggle each
  // independently or use Expand/Collapse all.
  const [openSections, setOpenSections] = useState(() => new Set([0]));
  // iter 94/95: rotating facts on the loading screen. We keep `factIdx`
  // and `factVisible` here; the actual list is resolved per render via
  // `factsForCity(destCity)` so a late-arriving destination_city swap
  // takes effect on the next tick.
  const [factIdx, setFactIdx] = useState(0);
  const [factVisible, setFactVisible] = useState(true);
  const chatEndRef = useRef(null);
  const pollingRef = useRef(null);

  // Fetch blueprint by ID or by user email
  const fetchBlueprint = useCallback(async () => {
    try {
      let data = null;
      
      // Try to fetch by ID first
      if (bpId) {
        const res = await fetch(`${API_URL}/api/blueprint/${bpId}`);
        if (res.ok) {
          data = await res.json();
        }
      }
      
      // If no ID or not found, try to fetch by user email
      if (!data && user?.email) {
        const res = await fetch(`${API_URL}/api/blueprint/by-email/${encodeURIComponent(user.email)}`);
        if (res.ok) {
          data = await res.json();
        }
      }
      
      if (data) {
        setBlueprint(data);
        setError(null);

        // Drive progress monotonically - never reset backwards.
        // Iter 66 - two-pass aware progress.
        // Iter 93 - slowed to reflect the actual 3–5 min generation time:
        //   Phase 1 (Foundation):   35% → 60% over ~135s (was 75s)
        //   Phase transition mark:  60% snap (phase_1_completed_at set)
        //   Phase 2 (Execution):    60% → 95% over ~135s (was 75s)
        //   Done:                   100%
        // Total time-based budget ≈ 4.5 min, matching the user-facing
        // "3–5 minutes" copy. Computed from elapsed time so refresh is
        // consistent across reloads.
        if (data.status === 'pending' || data.status === 'generating') {
          setIsGenerating(true);
          setGenerationProgress((prev) => {
            let timeFloor = 0;
            const started = data.generation_started_at;
            const phase1Done = data.phase_1_completed_at;
            const phase = data.generation_phase; // 'part_1' | 'part_2' | undefined

            if (started) {
              const elapsedMs = Date.now() - new Date(started).getTime();
              if (phase === 'part_2' || phase1Done) {
                // Phase 2 - baseline 60%, climb to 95% over ~135s after phase 1.
                const phase1ElapsedMs = phase1Done
                  ? new Date(phase1Done).getTime() - new Date(started).getTime()
                  : elapsedMs / 2;
                const phase2ElapsedMs = Math.max(0, elapsedMs - phase1ElapsedMs);
                const phase2Ratio = Math.max(0, Math.min(1, phase2ElapsedMs / 135_000));
                timeFloor = Math.round(60 + (95 - 60) * phase2Ratio);
              } else {
                // Phase 1 - 35% → 60% over ~135s.
                const phase1Ratio = Math.max(0, Math.min(1, elapsedMs / 135_000));
                timeFloor = Math.round(35 + (60 - 35) * phase1Ratio);
              }
            } else {
              timeFloor = data.status === 'generating' ? 35 : 15;
            }
            return Math.max(prev, timeFloor);
          });
          // Stash the active phase label so the UI can render it (e.g.
          // "Building visa pathway…" → "Building taxes, housing…").
          if (data.generation_phase_label && data.generation_phase_label !== blueprint?.generation_phase_label) {
            setBlueprint((bp) => bp ? { ...bp, generation_phase_label: data.generation_phase_label } : bp);
          }
        } else if (data.status === 'done') {
          setIsGenerating(false);
          setGenerationProgress(100);
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        } else if (data.status === 'error' || data.status === 'email_pending') {
          // Terminal failure states - stop polling and surface clearly so the
          // bar doesn't sit at 95% indefinitely. The UI below renders an
          // error banner when `blueprint.status === 'error'`.
          setIsGenerating(false);
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        } else {
          setIsGenerating(false);
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        }
        
        // Fetch subscription
        if (data.email) {
          fetch(`${API_URL}/api/blueprint/subscription/${data.email}`)
            .then(r => r.json())
            .then(setSubscription)
            .catch(() => {});
        }
      } else {
        // No blueprint found - check if user has a purchase
        if (user?.email) {
          const purchaseRes = await fetch(`${API_URL}/api/blueprint/check-purchase/${encodeURIComponent(user.email)}`);
          if (purchaseRes.ok) {
            const purchaseData = await purchaseRes.json();
            if (purchaseData.has_purchase && !purchaseData.blueprint_id) {
              // User has purchase but no blueprint yet - generation is
              // either queued or still spinning up. Use a monotonic floor so
              // the bar never snaps back to 10% on subsequent polls.
              setIsGenerating(true);
              setGenerationProgress((prev) => Math.max(prev, 10));
            } else if (!purchaseData.has_purchase) {
              setError('no_purchase');
            }
          } else {
            setError('not_found');
          }
        } else {
          setError('not_found');
        }
      }
    } catch (err) {
      console.error('Error fetching blueprint:', err);
      setError('connection_error');
    } finally {
      setLoading(false);
    }
  }, [bpId, user?.email]);

  // Initial fetch and polling for generating state
  useEffect(() => {
    fetchBlueprint();

    // iter 93 - slowed to match the real 3–5 min generation time. Was
    // `+5%` every 5s (would race to 95% in ~95s). Now `+2%` every 10s
    // ramps over ~7.5 min, but the time-based curve in `fetchBlueprint`
    // takes precedence via `Math.max(prev, timeFloor)` whenever the
    // backend has reported a `generation_started_at` timestamp.
    pollingRef.current = setInterval(() => {
      if (isGenerating) {
        fetchBlueprint();
        setGenerationProgress(prev => Math.min(prev + 2, 95));
      }
    }, 10000); // Poll every 10s

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [fetchBlueprint, isGenerating]);

  // iter 94/95: rotate facts every 8s while generation is active. Reads
  // the active list at swap time (so a destination_city update during
  // generation can change which set is shown without losing rotation).
  // Fade out → swap index → fade in (450ms each side) so the change feels
  // calm rather than abrupt. Effect is gated on `isGenerating` so it stops
  // immediately when the Blueprint is ready.
  useEffect(() => {
    if (!isGenerating) return undefined;
    const id = setInterval(() => {
      setFactVisible(false);
      setTimeout(() => {
        const list = factsForCity(
          blueprint?.city || user?.destination_city || null,
        );
        setFactIdx((i) => (i + 1) % list.length);
        setFactVisible(true);
      }, 450);
    }, 8000);
    return () => clearInterval(id);
  }, [isGenerating, blueprint?.city, user?.destination_city]);

  // Fetch chat history
  useEffect(() => {
    if (blueprint?.email) {
      fetch(`${API_URL}/api/blueprint/chat/history/${blueprint.email}`)
        .then(r => r.json())
        .then(data => {
          setMessages((data.messages || []).map(m => [
            { role: 'user', text: m.user_message },
            { role: 'assistant', text: m.assistant_message }
          ]).flat());
        })
        .catch(() => {});
    }
  }, [blueprint?.email]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // iter 184 - the raw `toggleItem` that POSTed to /api/blueprint/checklist/toggle
  // has been retired. All toggles now flow through ChecklistPanel → PUT /api/portal/checklist.

  const sendMessage = async (msg) => {
    const text = msg || input.trim();
    if (!text || chatLoading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setChatLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/blueprint/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: blueprint?.email || user?.email,
          message: text,
          blueprint_id: blueprint?.id || bpId,
        }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', text: data.response || 'Sorry, I could not process that.' }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Connection error. Please try again.' }]);
    }
    setChatLoading(false);
  };

  // iter 176 - Blueprint PDF download with proper UX.
  // Previous impl was a bare <a href> that gave zero feedback, took up to
  // 60s to render the PDF, and duplicate-downloaded on repeated clicks.
  // This version: fetches as blob, disables reentry for the duration,
  // surfaces loading + error state, ensures exactly ONE file per click.
  // Backend now caches PDFs (blueprint_pdfs collection) so subsequent
  // downloads are milliseconds; first-download-per-Blueprint is also
  // pre-warmed at completion time.
  const [pdfState, setPdfState] = useState({ status: 'idle', error: '' });
  const pdfAbortRef = useRef(null);

  const downloadPdf = useCallback(async () => {
    const blueprintId = blueprint?.id || bpId;
    if (!blueprintId) return;
    if (pdfState.status === 'loading') return; // block reentry while a download is in flight

    setPdfState({ status: 'loading', error: '' });
    const controller = new AbortController();
    pdfAbortRef.current = controller;

    try {
      const res = await fetch(`${API_URL}/api/blueprint/pdf/${blueprintId}`, {
        method: 'GET',
        credentials: 'include',
        signal: controller.signal,
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const filename = `Mundalo_Blueprint_${(blueprint?.name || 'Relocation').replace(/\s+/g, '_')}.pdf`;
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      // Clean up the DOM node + object URL on the next tick so Safari
      // still gets a chance to hand the download off.
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);
      }, 500);
      setPdfState({ status: 'success', error: '' });
    } catch (err) {
      if (err.name === 'AbortError') {
        setPdfState({ status: 'idle', error: '' });
        return;
      }
      setPdfState({
        status: 'error',
        error: err.message || 'Could not prepare your Blueprint.',
      });
    } finally {
      pdfAbortRef.current = null;
    }
  }, [blueprint, bpId, pdfState.status]);

  useEffect(() => {
    return () => {
      if (pdfAbortRef.current) pdfAbortRef.current.abort();
    };
  }, []);

  // iter 89: parse the Blueprint markdown into collapsible sections.
  // MUST run before any early returns to satisfy the Rules of Hooks.
  const sections = useMemo(
    () => parseBlueprintSections(blueprint?.content),
    [blueprint?.content],
  );

  // iter 100 - Auth guard. Placed AFTER all hooks (per rules-of-hooks)
  // but before the loading/error early returns so an unauthenticated
  // visitor doesn't sit on the "Loading your Blueprint…" screen forever.
  if (!user && !authLoading) return <Navigate to="/portal" replace />;

  // Loading state
  if (loading) {
    return (
      <PortalLayout activeTab="blueprint">
        <div className="min-h-screen bg-[#F9F6EF] flex items-center justify-center p-4">
          <div className="text-center">
            <Loader2 className="w-10 h-10 text-[#2CA8A0] mx-auto animate-spin" />
            <p className="text-[#888] mt-4">Loading your Blueprint...</p>
          </div>
        </div>
      </PortalLayout>
    );
  }

  // Generating state with live progress
  if (isGenerating) {
    // iter 93: prefer the user's first name from their profile; fall back
    // to "you" if it isn't available yet (rare - onboarding sets it before
    // the first Blueprint request).
    const firstName =
      (user?.first_name && user.first_name.trim()) ||
      (blueprint?.name && String(blueprint.name).trim().split(' ')[0]) ||
      'you';

    // iter 95: pick the city-specific fact list when we know the
    // destination, otherwise fall back to the generic Mexico list.
    const activeFacts = factsForCity(
      blueprint?.city || user?.destination_city || null,
    );
    // Guard the index - if a city swap shrinks the list, the previous
    // index might be out of range for one render until the rotation
    // effect catches up.
    const safeIdx = activeFacts.length ? factIdx % activeFacts.length : 0;

    return (
      <PortalLayout activeTab="blueprint">
        <div className="min-h-screen bg-[#F9F6EF] p-4 sm:p-8" data-testid="blueprint-generating-page">
        <div className="max-w-2xl mx-auto pt-12 sm:pt-20">
          <div className="text-center mb-12">
            <div className="relative w-24 h-24 mx-auto mb-8">
              <div className="absolute inset-0 border-4 border-[#EFE6CF] rounded-full" />
              <div
                // iter 93: slowed spinner from 1.5s/rev → 4s/rev so the
                // motion matches the unhurried 3–5 min generation pace
                // (fast spin felt frantic + suggested an instant result).
                className="absolute inset-0 border-4 border-[#2CA8A0] rounded-full border-t-transparent animate-spin"
                style={{ animationDuration: '4s' }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl text-[#2CA8A0] font-light" data-testid="blueprint-progress-pct">{generationProgress}%</span>
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl text-[#0A1F2E] font-display italic mb-4">
              Building your Blueprint
            </h1>
            <p className="text-[#0A1F2E]/70 max-w-md mx-auto mb-3" data-testid="blueprint-generating-subtext">
              This usually takes 3–5 minutes. We're building the most personalized relocation guide for {firstName}.
            </p>
            {blueprint?.generation_phase_label && (
              <p
                className="text-[#1A5C6E] text-xs max-w-md mx-auto mb-8 font-mono uppercase tracking-[0.15em]"
                data-testid="blueprint-phase-label"
              >
                {blueprint.generation_phase_label}
              </p>
            )}
            {!blueprint?.generation_phase_label && <div className="mb-8" />}
            
            {/* Progress steps - iter 66: 6 progressive sections matching the
                two-pass split so the user sees real movement, not a generic bar. */}
            <div className="bg-white border border-[#EFE6CF] rounded-2xl p-6 text-left max-w-md mx-auto shadow-sm" data-testid="blueprint-phase-steps">
              <div className="space-y-3">
                {/* PHASE 1 - Foundation */}
                <div className="flex items-center gap-3" data-testid="phase-step-1">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${generationProgress >= 40 ? 'bg-[#2CA8A0]' : 'bg-[#EFE6CF]'}`}>
                    {generationProgress >= 40 ? <Check className="w-4 h-4 text-white" /> : <span className="text-xs text-[#0A1F2E]/40">1</span>}
                  </div>
                  <span className={`text-sm ${generationProgress >= 40 ? 'text-[#0A1F2E]' : 'text-[#0A1F2E]/40'}`}>Visa &amp; legal pathway</span>
                </div>
                <div className="flex items-center gap-3" data-testid="phase-step-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${generationProgress >= 48 ? 'bg-[#2CA8A0]' : 'bg-[#EFE6CF]'}`}>
                    {generationProgress >= 48 ? <Check className="w-4 h-4 text-white" /> : <span className="text-xs text-[#0A1F2E]/40">2</span>}
                  </div>
                  <span className={`text-sm ${generationProgress >= 48 ? 'text-[#0A1F2E]' : 'text-[#0A1F2E]/40'}`}>Neighborhoods &amp; trendy spots</span>
                </div>
                <div className="flex items-center gap-3" data-testid="phase-step-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${generationProgress >= 58 ? 'bg-[#2CA8A0]' : 'bg-[#EFE6CF]'}`}>
                    {generationProgress >= 58 ? <Check className="w-4 h-4 text-white" /> : <span className="text-xs text-[#0A1F2E]/40">3</span>}
                  </div>
                  <span className={`text-sm ${generationProgress >= 58 ? 'text-[#0A1F2E]' : 'text-[#0A1F2E]/40'}`}>Banking &amp; healthcare</span>
                </div>
                {/* PHASE 2 - Execution */}
                <div className="flex items-center gap-3 pt-3 border-t border-[#EFE6CF]" data-testid="phase-step-4">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${generationProgress >= 72 ? 'bg-[#2CA8A0]' : 'bg-[#EFE6CF]'}`}>
                    {generationProgress >= 72 ? <Check className="w-4 h-4 text-white" /> : <span className="text-xs text-[#0A1F2E]/40">4</span>}
                  </div>
                  <span className={`text-sm ${generationProgress >= 72 ? 'text-[#0A1F2E]' : 'text-[#0A1F2E]/40'}`}>Taxes, housing, cost of living</span>
                </div>
                <div className="flex items-center gap-3" data-testid="phase-step-5">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${generationProgress >= 84 ? 'bg-[#2CA8A0]' : 'bg-[#EFE6CF]'}`}>
                    {generationProgress >= 84 ? <Check className="w-4 h-4 text-white" /> : <span className="text-xs text-[#0A1F2E]/40">5</span>}
                  </div>
                  <span className={`text-sm ${generationProgress >= 84 ? 'text-[#0A1F2E]' : 'text-[#0A1F2E]/40'}`}>Moving plan, vehicle, safety</span>
                </div>
                <div className="flex items-center gap-3" data-testid="phase-step-6">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${generationProgress >= 92 ? 'bg-[#2CA8A0]' : 'bg-[#EFE6CF]'}`}>
                    {generationProgress >= 92 ? <Check className="w-4 h-4 text-white" /> : <span className="text-xs text-[#0A1F2E]/40">6</span>}
                  </div>
                  <span className={`text-sm ${generationProgress >= 92 ? 'text-[#0A1F2E]' : 'text-[#0A1F2E]/40'}`}>Your 30-day action checklist</span>
                </div>
              </div>
            </div>

            {/* iter 94 - rotating Mexico facts. Cycles every 8s with a soft
                fade so the wait feels productive instead of empty. Cream
                surface, teal left border, italic Cormorant copy, mono
                "Did you know?" eyebrow - matches the rest of the portal. */}
            <div
              className="bg-[#FCF8EE] border border-[#EFE6CF] border-l-4 border-l-[#2CA8A0] rounded-2xl p-5 sm:p-6 text-left max-w-md mx-auto mt-5"
              data-testid="blueprint-fact-card"
              aria-live="polite"
              aria-atomic="true"
            >
              <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-2">
                Did you know?
              </p>
              <p
                className={`font-display italic text-lg sm:text-xl text-[#0A1F2E] leading-snug min-h-[3.5rem] transition-opacity duration-500 ${factVisible ? 'opacity-100' : 'opacity-0'}`}
                data-testid="blueprint-fact-text"
                key={safeIdx}
              >
                {activeFacts[safeIdx]}
              </p>
            </div>
          </div>
        </div>
        </div>
      </PortalLayout>
    );
  }

  // Generation failure state - explicit terminal status. Better than the
  // generic "not found" because we KNOW the user paid + the task ran but
  // failed (Claude error, timeout, etc). Iter 65: previously the bar sat
  // at 95% forever; now we show a clear error + retry path.
  if (blueprint && (blueprint.status === 'error' || blueprint.status === 'email_pending')) {
    const isEmailPending = blueprint.status === 'email_pending';
    return (
      <PortalLayout activeTab="blueprint">
        <div className="min-h-screen bg-[#F9F6EF] p-4 sm:p-8" data-testid="blueprint-generation-error-page">
          <div className="max-w-lg mx-auto pt-12 sm:pt-20 text-center">
            <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-[#F4ECD8] rounded-full">
              <AlertCircle className="w-8 h-8 text-[#E8A830]" />
            </div>
            <h1 className="text-2xl sm:text-3xl text-[#0A1F2E] font-display italic mb-4">
              {isEmailPending ? "We're finishing it by email." : "Something interrupted your Blueprint."}
            </h1>
            <p className="text-[#0A1F2E]/70 mb-6 max-w-md mx-auto leading-relaxed">
              {isEmailPending
                ? "Our first pass had a hiccup. We're retrying now and we'll email your finished Blueprint within a few minutes - keep an eye on your inbox."
                : "We hit a snag while generating your Blueprint. Your purchase is safe and your answers are saved. Please contact us and we'll regenerate it within the hour."}
            </p>
            <div className="bg-white border border-[#EFE6CF] rounded-2xl p-6 mb-4">
              <Mail className="w-7 h-7 text-[#2CA8A0] mx-auto mb-3" />
              <p className="text-[#0A1F2E] mb-1 font-medium">Get a human on it</p>
              <a
                href="mailto:hello@mundalo.com?subject=Blueprint%20generation%20issue"
                className="text-[#2CA8A0] hover:underline text-lg"
                data-testid="blueprint-error-contact"
              >
                hello@mundalo.com
              </a>
              <p className="text-[#0A1F2E]/55 text-xs mt-3 font-mono uppercase tracking-wider">
                Blueprint ID: {blueprint.id?.slice(0, 8)}
              </p>
            </div>
          </div>
        </div>
      </PortalLayout>
    );
  }

  // Error state - show contact message instead of redirecting
  if (error || !blueprint) {
    return (
      <PortalLayout activeTab="blueprint">
        <div className="min-h-screen bg-[#F9F6EF] p-4 sm:p-8" data-testid="blueprint-error-page">
        <div className="max-w-lg mx-auto pt-12 sm:pt-20 text-center">
          <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-[#FFFFFF] border border-[#333] rounded-full">
            <AlertCircle className="w-8 h-8 text-[#2CA8A0]" />
          </div>
          <h1 className="text-2xl text-[#0A1F2E] font-display italic mb-4">
            {error === 'no_purchase' ? 'No Active Subscription Found' : 'Blueprint Not Found'}
          </h1>
          <p className="text-[#0A1F2E]/60 mb-8">
            {error === 'no_purchase' 
              ? "We couldn't find an active subscription for your account. If you recently purchased, please wait a few minutes and refresh."
              : "We couldn't locate your Blueprint. If you believe this is an error, please contact us and we'll help you right away."
            }
          </p>
          <div className="bg-white border border-[#EFE6CF] rounded-2xl p-6 shadow-sm">
            <Mail className="w-8 h-8 text-[#2CA8A0] mx-auto mb-4" />
            <p className="text-[#0A1F2E] mb-2">Need help?</p>
            <a 
              href="mailto:hello@mundalo.com" 
              className="text-[#1A5C6E] hover:underline text-lg"
            >
              hello@mundalo.com
            </a>
            <p className="text-[#0A1F2E]/50 text-sm mt-4">
              Include your email address and we'll resolve this within 24 hours.
            </p>
          </div>
        </div>
        </div>
      </PortalLayout>
    );
  }

  // iter 184 - checklist progress + grouping now computed by ChecklistPanel
  // via /api/portal/checklist. The raw blueprint.checklist derivation used
  // to live here.

  // iter 89: derived helpers around the section list (parsed at top of component).
  const allOpen = sections.length > 0 && openSections.size === sections.length;
  const toggleSection = (idx) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };
  const expandAll = () => setOpenSections(new Set(sections.map((_, i) => i)));
  const collapseAll = () => setOpenSections(new Set());
  const jumpToSection = (idx) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
    // Defer scroll until after the section has rendered open.
    setTimeout(() => {
      document
        .getElementById(`bp-section-${idx}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  return (
    <PortalLayout activeTab="blueprint">
      <div className="min-h-screen bg-[#F9F6EF] p-4 sm:p-8" data-testid="portal-blueprint-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <p className="text-[10px] tracking-[0.15em] uppercase text-[#1A5C6E] font-mono mb-2">Your relocation plan</p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-2">
            <h1 className="text-3xl sm:text-4xl text-[#0A1F2E] font-display italic">My Blueprint</h1>
            {/* iter 106 - Guided-tier "Reviewed by a Mundalo Advisor" badge.
                Mirrors the PDF cover seal (iter 105) so Guided buyers see
                the VIP cue both in the portal and on their downloaded PDF. */}
            {user?.role === 'guided' && (
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-[#1A5C6E]/50 bg-[#FCF8EE] text-[#1A5C6E] font-mono uppercase tracking-[0.12em] text-[10px] sm:text-[11px] font-semibold"
                data-testid="advisor-reviewed-badge"
                title="Reviewed by a Mundalo Advisor"
              >
                <ShieldCheck className="w-3 h-3" strokeWidth={2} />
                Reviewed by a Mundalo Advisor
              </span>
            )}
          </div>
          <p className="text-[#0A1F2E]/60 text-sm sm:text-base">
            Personalized for {blueprint.city || 'Mexico'} - generated {blueprint.created_at ? new Date(blueprint.created_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago' }) : ''}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 sm:gap-2 mb-6 sm:mb-8 border-b border-[#EFE6CF] overflow-x-auto">
          <button
            onClick={() => setTab('blueprint')}
            className={`flex items-center gap-2 px-3 sm:px-4 py-3 text-sm whitespace-nowrap transition ${tab === 'blueprint' ? 'text-[#1A5C6E] border-b-2 border-[#1A5C6E]' : 'text-[#0A1F2E]/50 hover:text-[#0A1F2E]'}`}
            data-testid="tab-blueprint"
          >
            <FileText className="w-4 h-4" /> My Blueprint
          </button>
          <button
            onClick={() => setTab('checklist')}
            className={`flex items-center gap-2 px-3 sm:px-4 py-3 text-sm whitespace-nowrap transition ${tab === 'checklist' ? 'text-[#1A5C6E] border-b-2 border-[#1A5C6E]' : 'text-[#0A1F2E]/50 hover:text-[#0A1F2E]'}`}
            data-testid="tab-checklist"
          >
            <ClipboardList className="w-4 h-4" /> Checklist
          </button>
          <button
            onClick={() => setTab('ai')}
            className={`flex items-center gap-2 px-3 sm:px-4 py-3 text-sm whitespace-nowrap transition ${tab === 'ai' ? 'text-[#1A5C6E] border-b-2 border-[#1A5C6E]' : 'text-[#0A1F2E]/50 hover:text-[#0A1F2E]'}`}
            data-testid="tab-ai"
          >
            <MessageSquare className="w-4 h-4" /> Assistant
          </button>
        </div>

        {/* Blueprint Tab */}
        {tab === 'blueprint' && (
          <div data-testid="blueprint-content">
            {/* Toolbar - Download + Expand/Collapse */}
            <div className="flex flex-wrap items-center justify-end gap-2 mb-4">
              {sections.length > 1 && (
                <button
                  type="button"
                  onClick={allOpen ? collapseAll : expandAll}
                  className="flex items-center gap-2 bg-white border border-[#EFE6CF] text-[#0A1F2E] px-4 py-2 text-sm rounded-full hover:bg-[#FCF8EE] transition"
                  data-testid="bp-toggle-all-btn"
                >
                  {allOpen ? (
                    <><ChevronsDownUp className="w-4 h-4" /> Collapse all</>
                  ) : (
                    <><ChevronsUpDown className="w-4 h-4" /> Expand all</>
                  )}
                </button>
              )}
              <div className="flex flex-col items-end gap-1">
                <button
                  onClick={downloadPdf}
                  disabled={pdfState.status === 'loading'}
                  className={`flex items-center gap-2 text-white px-5 py-2.5 text-sm font-medium rounded-full transition ${
                    pdfState.status === 'loading'
                      ? 'bg-[#5A6B73] cursor-wait'
                      : pdfState.status === 'error'
                        ? 'bg-[#B94A3B] hover:bg-[#A03D31]'
                        : 'bg-[#0A1F2E] hover:bg-[#163448]'
                  }`}
                  data-testid="download-pdf-btn"
                  aria-live="polite"
                  aria-busy={pdfState.status === 'loading'}
                >
                  {pdfState.status === 'loading' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Preparing your Blueprint…
                    </>
                  ) : pdfState.status === 'error' ? (
                    <>
                      <AlertCircle className="w-4 h-4" />
                      Retry download
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" /> Download PDF
                    </>
                  )}
                </button>
                {pdfState.status === 'error' && (
                  <p
                    className="text-[11px] text-[#B94A3B]"
                    data-testid="download-pdf-error"
                    role="alert"
                  >
                    Something went wrong ({pdfState.error}). Click Retry.
                  </p>
                )}
              </div>
            </div>

            {blueprint.content ? (
              <>
                {/* Section index - clickable, jumps to + opens a section */}
                {sections.length > 1 && (
                  <div
                    className="bg-white border border-[#EFE6CF] rounded-2xl p-5 sm:p-6 shadow-sm mb-5"
                    data-testid="bp-section-index"
                  >
                    <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-3">
                      On this page
                    </p>
                    <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                      {sections.map((s, i) => (
                        <li key={i}>
                          <button
                            type="button"
                            onClick={() => jumpToSection(i)}
                            className="group flex items-baseline gap-2 text-left w-full py-1 text-[#0A1F2E] hover:text-[#1A5C6E] transition"
                            data-testid={`bp-index-link-${i}`}
                          >
                            <span className="font-mono text-[10px] tracking-wider text-[#1A5C6E]/70 group-hover:text-[#1A5C6E]">
                              {String(i + 1).padStart(2, '0')}
                            </span>
                            <span className="text-sm sm:text-[15px] leading-snug">
                              {s.title}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Section cards - collapsible */}
                <div className="space-y-3" data-testid="bp-sections">
                  {sections.map((s, i) => {
                    const open = openSections.has(i);
                    const SectionIcon = getSectionIcon(s.title);
                    const { number, cleanTitle } = splitSectionNumber(s.title);
                    return (
                      <div
                        key={i}
                        id={`bp-section-${i}`}
                        className="bg-white border border-[#EFE6CF] rounded-2xl shadow-sm overflow-hidden scroll-mt-6"
                        data-testid={`bp-section-${i}`}
                      >
                        <button
                          type="button"
                          onClick={() => toggleSection(i)}
                          className="w-full flex items-start justify-between gap-4 text-left px-5 sm:px-8 py-5 hover:bg-[#FCF8EE] transition"
                          data-testid={`bp-section-toggle-${i}`}
                          aria-expanded={open}
                        >
                          <div className="flex items-start gap-4 flex-1 min-w-0">
                            {/* iter 103 - section icon + number badge for a
                                consultant-report feel. */}
                            <div className="shrink-0 mt-1 w-10 h-10 rounded-full bg-[#FCF8EE] border border-[#EFE6CF] flex items-center justify-center text-[#1A5C6E]">
                              <SectionIcon className="w-5 h-5" strokeWidth={1.6} />
                            </div>
                            <div className="flex-1 min-w-0">
                              {number && (
                                <p
                                  className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#1A5C6E] mb-1"
                                  data-testid={`bp-section-number-${i}`}
                                >
                                  Section {number}
                                </p>
                              )}
                              <h2 className="font-display italic text-2xl sm:text-3xl text-[#0A1F2E] leading-tight">
                                {cleanTitle}
                              </h2>
                            </div>
                          </div>
                          <div className="shrink-0 pt-2 text-[#1A5C6E]">
                            {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                          </div>
                        </button>
                        {open && (
                          <div className="px-5 sm:px-8 pb-7 sm:pb-9 border-t border-[#EFE6CF]/70">
                            <div className="pt-5 max-w-none" data-testid={`bp-section-body-${i}`}>
                              <ReactMarkdown remarkPlugins={[remarkGfm]} components={blueprintMarkdownComponents}>
                                {s.body}
                              </ReactMarkdown>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="bg-white border border-[#EFE6CF] rounded-2xl p-6 sm:p-10 shadow-sm">
                <p className="text-[#0A1F2E]/50 text-center py-12">
                  Blueprint content is being prepared...
                </p>
              </div>
            )}

            {/* Viator affiliate - post-Blueprint success CTA (iter 77).
                Lives at the bottom of the primary Blueprint view so it surfaces
                immediately after the user receives their plan. */}
            <div className="mt-6">
              <ViatorCtaCard
                eyebrow="Experiences & Tours"
                headline="Ready to explore?"
                subtext="Discover tours and experiences in your new city."
                testId="viator-cta-blueprint"
                surface="blueprint"
                cityName={blueprint?.city || null}
                userRole={user?.role || null}
              />
            </div>
          </div>
        )}

        {/* Checklist Tab - iter 184: rendered by the same shared
            ChecklistPanel as /portal/checklist. Data source is
            /api/portal/checklist (AI-extracted, correlated, iter 183
            reference-safe). The raw blueprint.checklist render was
            retired here; toggles now route through PUT /api/portal/
            checklist. */}
        {tab === 'checklist' && (
          <div data-testid="checklist-content">
            <ChecklistPanel showHeader={false} emptyMessage="Your checklist will appear here once your Blueprint is generated." />
          </div>
        )}

        {/* Assistant Tab */}
        {tab === 'ai' && (
          <div className="flex flex-col h-[calc(100vh-300px)] min-h-[400px]" data-testid="ai-chat-content">
            {/* Chat messages */}
            <div className="flex-1 overflow-y-auto bg-white border border-[#EFE6CF] rounded-2xl p-4 mb-4 shadow-sm">
              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 text-[#0A1F2E]/20 mx-auto mb-4" />
                  <p className="text-[#0A1F2E]/60 mb-6">Ask your relocation assistant anything</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {suggestedQuestions.map((q, i) => (
                      <button
                        key={i}
                        onClick={() => sendMessage(q)}
                        className="text-sm px-3 py-2 bg-[#F4ECD8] text-[#0A1F2E] hover:bg-[#EFE6CF] transition rounded-full"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] sm:max-w-[75%] p-3 sm:p-4 rounded-2xl ${msg.role === 'user' ? 'bg-[#2CA8A0] text-white rounded-br-sm' : 'bg-white border border-[#EFE6CF] text-[#0A1F2E] rounded-bl-sm'}`}>
                        <p className="text-sm sm:text-base whitespace-pre-wrap">{msg.text}</p>
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-white border border-[#EFE6CF] p-4 rounded-2xl rounded-bl-sm">
                        <Loader2 className="w-5 h-5 text-[#2CA8A0] animate-spin" />
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
              )}
            </div>

            {/* Chat input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Ask about your relocation..."
                className="flex-1 bg-white border border-[#EFE6CF] text-[#0A1F2E] px-4 py-3 text-sm sm:text-base rounded-full focus:border-[#1A5C6E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/25"
                data-testid="chat-input"
              />
              <button
                onClick={() => sendMessage()}
                disabled={chatLoading || !input.trim()}
                className="bg-[#2CA8A0] text-white px-5 py-3 rounded-full hover:bg-[#1A5C6E] transition disabled:opacity-50"
                data-testid="chat-send-btn"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Call booking section - only for higher tiers */}
        {['kickstart', 'full_coordination', 'navigator', 'concierge'].includes(subscription?.tier) && (
          <div className="mt-8 border border-[#2CA8A0]/30 bg-[#2CA8A0]/5 rounded-2xl p-4 sm:p-8" data-testid="portal-booking-section">
            <h3 className="text-lg font-display italic text-[#0A1F2E] mb-2">Book Your Advisory Call</h3>
            <p className="text-[#0A1F2E]/60 text-sm mb-4">
              Your plan includes advisory calls with your Mundalo advisor. Get personalized guidance for your relocation.
            </p>
            <a
              href="https://cal.com/mundalo"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-[#E8A830] text-[#0A1F2E] font-semibold px-6 py-2.5 text-sm rounded-full hover:bg-[#D6991F] transition"
              data-testid="portal-book-advisory-btn"
            >
              Schedule Your Call
            </a>
          </div>
        )}
      </div>
      </div>
    </PortalLayout>
  );
}
